import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import { IoMdArrowForward } from "react-icons/io";
import { FiArrowRight, FiArrowLeft } from "react-icons/fi";

// Asset Imports
import bannerMutton from "../assets/avocados.jpeg";
import bannerBeans from "../assets/banner3.jpg";
import bannerSesame from "../assets/bananas.jpeg";

const SLIDE_DURATION = 6000;

export default function HomeBanner() {
  const { navTo } = useContext(AppContext);
  const [isReady, setIsReady] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);

  const slides = useMemo(() => [
    {
      image: bannerMutton,
      tagline: "Quality Sourced. Globally Delivered.",
      title: <>Premium Tanzanian <br/> <span className="text-accent font-bold">Mutton & Avocados</span></>,
      description: "Leading the way in processing and exporting high-grade produce to the Middle East and beyond.",
    },
    {
      image: bannerBeans,
      tagline: "Sustainable Trade Roots",
      title: <>Ethical Agriculture <br/> <span className="text-accent font-bold">& Community Support</span></>,
      description: "Empowering local Tanzanian farmers through eco-friendly practices and transparent supply chains.",
    },
    {
      image: bannerSesame,
      tagline: "International Standards",
      title: <>Certified Organic <br/> <span className="text-accent font-bold">Produce & Marine Life</span></>,
      description: "Delivering safe, hygienic products via superior cold-chain logistics and global compliance.",
    },
  ], []);

  const nextSlide = useCallback(() => {
    setSlideIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setSlideIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    setIsReady(true);
    const interval = setInterval(nextSlide, SLIDE_DURATION);
    return () => clearInterval(interval);
  }, [nextSlide]);

  if (!isReady) return <div className="loader-full">{getInlineLoader()}</div>;

  return (
    <section className="modern-hero pt-5">
      {/* Background Images Layer */}
      {slides.map((slide, index) => (
        <div 
          key={index}
          className={`hero-bg ${index === slideIndex ? 'active' : ''}`}
          style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.5)), url(${slide.image})` }}
        />
      ))}

      <div className="container hero-container">
        <div className="row align-items-center min-vh-100">
          <div className="col-lg-8 col-xl-7">
            
            <div className="hero-content">
              {slides.map((slide, index) => (
                <div key={index} className={`slide-info ${index === slideIndex ? 'active' : ''}`}>
                  
                  <h1 className="hero-display">{slide.title}</h1>
                  <p className="hero-para">{slide.description}</p>
                  
                  <div className="hero-actions">
                    <button onClick={() => navTo({ item: "services" })} className="btn-modern-primary">
                      Explore Our Products <IoMdArrowForward className="icon" />
                    </button>
                  </div>

                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      <style>{`
        .modern-hero { height: 100vh; position: relative; overflow: hidden; color: #fff; background: #000; }
        .hero-bg { 
          position: absolute; inset: 0; background-position: center; background-size: cover; 
          opacity: 0; transition: opacity 1.2s ease-in-out, transform 8s linear; 
          transform: scale(1.1); z-index: 1; 
        }
        .hero-bg.active { opacity: 1; transform: scale(1); z-index: 2; }
        
        .hero-container { position: relative; z-index: 5; }
        .tagline-wrap { display: flex; align-items: center; gap: 15px; margin-bottom: 20px; }
        .tagline-wrap .line { height: 2px; width: 40px; background: #f9c96c; display: block; }
        .tagline-text { color: #f9c96c; font-weight: 700; text-uppercase; letter-spacing: 2px; font-size: 0.9rem; }
        
        .hero-display { font-size: 4.5rem; font-weight: 800; line-height: 1.1; margin-bottom: 25px; }
        .text-accent { color: #8DAE81; }
        .hero-para { font-size: 1.25rem; opacity: 0.9; max-width: 550px; margin-bottom: 40px; }
        
        .slide-info { display: none; }
        .slide-info.active { display: block; animation: fadeInUp 0.8s ease forwards; }
        
        .btn-modern-primary { 
          padding: 18px 35px; background: white; color: #252523; border: none; 
          border-radius: 4px; font-weight: 700; transition: 0.3s; display: inline-flex; align-items: center; gap: 10px;
        }
        .btn-modern-primary:hover { background: #fff; transform: translateY(-3px); }
        .btn-modern-ghost { 
          padding: 18px 35px; background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.3); 
          border-radius: 4px; font-weight: 700; margin-left: 20px; transition: 0.3s;
        }
        .btn-modern-ghost:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

        .hero-footer { 
          position: absolute; bottom: 50px; left: 0; right: 0; z-index: 10;
          display: flex; justify-content: space-between; align-items: center;
        }
        .hero-nav-controls { display: flex; align-items: center; gap: 30px; }
        .nav-arrow { background: none; border: 1px solid rgba(255,255,255,0.2); color: #fff; width: 50px; height: 50px; border-radius: 50%; transition: 0.3s; }
        .nav-arrow:hover { background: #f9c96c; color: #000; border-color: #f9c96c; }

        .progress-dots { display: flex; gap: 15px; }
        .dot { width: 40px; height: 3px; background: rgba(255,255,255,0.2); cursor: pointer; position: relative; }
        .dot.active .filler { 
          height: 100%; background: #f9c96c; width: 0;
          animation: progressRun ${SLIDE_DURATION}ms linear forwards; 
        }

        .slide-counter { font-size: 1.5rem; font-weight: 700; }
        .slide-counter .current { color: #f9c96c; }
        .slide-counter .total { opacity: 0.5; margin-left: 5px; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes progressRun {
          from { width: 0%; }
          to { width: 100%; }
        }

        @media (max-width: 991px) {
          .hero-display { font-size: 3rem; }
          .btn-modern-ghost { margin-left: 0; margin-top: 15px; display: block; text-align: center; }
        }
      `}</style>
    </section>
  );
}